import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Check,
  CircleOff,
  ClipboardPaste,
  Columns2,
  Copy,
  Eye,
  LineChart,
  Layers3,
  Palette,
  Redo2,
  Save,
  Trash2,
  Undo2,
} from 'lucide-react'
import { useShallow } from 'zustand/react/shallow'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useKeyframesStore, useTimelineStore } from '@/features/effects/deps/timeline-contract'
import { useGizmoStore, useThrottledFrame } from '@/features/effects/deps/preview-contract'
import { useColorGradeActions } from '@/features/effects/hooks/use-color-grade-actions'
import {
  useColorGradeUndoStore,
  getGradeCanUndo,
  getGradeCanRedo,
  undoGrade,
  redoGrade,
  applyRestoredEffects,
} from '@/features/effects/stores/color-grade-undo-store'
import { COLOR_GRADE_EFFECT_TYPES } from '@/features/effects/utils/color-grade'
import { GpuCurvesPanel, GpuWheelsPanel } from './panels'
import { getGpuEffect, getGpuEffectDefaultParams } from '@/infrastructure/gpu/effects'
import {
  getAutoKeyframeOperation,
  getResolvedAnimatedEffectParamValue,
} from '@/features/effects/deps/keyframes-contract'
import { buildEffectAnimatableProperty, type AnimatableProperty } from '@/types/keyframe'
import type { ItemEffect, GpuEffect } from '@/types/effects'
import type { TimelineItem } from '@/types/timeline'
import { useEditorStore } from '@/app/state/editor'

interface ColorGradeSectionProps {
  items: TimelineItem[]
  layout?: 'sidebar' | 'dock'
  onCreateAdjustmentLayer?: () => void
}

function findGradeEntry(
  item: TimelineItem | null | undefined,
  effectType: (typeof COLOR_GRADE_EFFECT_TYPES)[number],
): ItemEffect | null {
  if (!item) return null
  return (
    item.effects?.find(
      (effect) =>
        effect.effect.type === 'gpu-effect' && effect.effect.gpuEffectType === effectType,
    ) ?? null
  )
}

export const ColorGradeSection = memo(function ColorGradeSection({
  items,
  layout = 'sidebar',
  onCreateAdjustmentLayer,
}: ColorGradeSectionProps) {
  const addEffect = useTimelineStore((state) => state.addEffect)
  const updateEffect = useTimelineStore((state) => state.updateEffect)
  const removeEffect = useTimelineStore((state) => state.removeEffect)
  const toggleEffect = useTimelineStore((state) => state.toggleEffect)
  const applyAutoKeyframeOperations = useTimelineStore((state) => state.applyAutoKeyframeOperations)
  const setEffectsPreviewNew = useGizmoStore((state) => state.setEffectsPreviewNew)
  const clearPreview = useGizmoStore((state) => state.clearPreview)
  const colorScopesOpen = useEditorStore((state) => state.colorScopesOpen)
  const toggleColorScopesOpen = useEditorStore((state) => state.toggleColorScopesOpen)
  const comparisonMode = useEditorStore((state) => state.colorComparisonMode)
  const setComparisonMode = useEditorStore((state) => state.setColorComparisonMode)
  const currentFrame = useThrottledFrame()
  const itemIds = useMemo(() => items.map((item) => item.id), [items])
  const displayItem = items[0] ?? null
  const [presetName, setPresetName] = useState('')
  const [presetPopoverOpen, setPresetPopoverOpen] = useState(false)
  const {
    presets,
    canCopyGrade,
    canPasteGrade,
    canSavePreset,
    copyGrade,
    pasteGrade,
    savePreset,
    applyPreset,
    removePreset,
  } = useColorGradeActions(items)

  const itemKeyframeList = useKeyframesStore(
    useShallow(
      useCallback(
        (state) => itemIds.map((itemId) => state.keyframesByItemId[itemId] ?? null),
        [itemIds],
      ),
    ),
  )
  const itemKeyframes = useMemo(() => {
    const map = new Map<string, (typeof itemKeyframeList)[number]>()
    for (const [index, itemId] of itemIds.entries()) {
      map.set(itemId, itemKeyframeList[index] ?? null)
    }
    return map
  }, [itemIds, itemKeyframeList])

  useEffect(() => {
    for (const item of items) {
      const existingTypes = new Set(
        (item.effects ?? [])
          .filter((effect) => effect.effect.type === 'gpu-effect')
          .map((effect) => effect.effect.gpuEffectType),
      )

      for (const effectType of COLOR_GRADE_EFFECT_TYPES) {
        if (existingTypes.has(effectType)) {
          continue
        }

        addEffect(item.id, {
          type: 'gpu-effect',
          gpuEffectType: effectType,
          params: getGpuEffectDefaultParams(effectType),
        })
      }
    }
  }, [addEffect, items])

  // Initialize undo store with current effects when items change
  useEffect(() => {
    const effectsByItem: Record<string, ItemEffect[]> = {}
    for (const item of items) {
      effectsByItem[item.id] = [...(item.effects ?? [])]
    }
    useColorGradeUndoStore.getState().clearHistory()
    useColorGradeUndoStore.getState().recordBeforeChange(effectsByItem)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemIds.join(',')])
  // Use itemIds.join(',') as dependency to avoid re-init when only array reference changes

  // Track undo/redo availability for UI
  const [canGradeUndo, setCanGradeUndo] = useState(false)
  const [canGradeRedo, setCanGradeRedo] = useState(false)

  const updateUndoState = useCallback(() => {
    setCanGradeUndo(getGradeCanUndo())
    setCanGradeRedo(getGradeCanRedo())
  }, [])

  useEffect(() => {
    updateUndoState()
    const unsub = useColorGradeUndoStore.subscribe(updateUndoState)
    return unsub
  }, [updateUndoState])

  const hasEnabledGrade = useMemo(
    () =>
      items.some((item) =>
        COLOR_GRADE_EFFECT_TYPES.some((effectType) => {
          const entry = findGradeEntry(item, effectType)
          return entry?.enabled ?? false
        }),
      ),
    [items],
  )

  useEffect(() => {
    if (!hasEnabledGrade && comparisonMode !== 'off') {
      setComparisonMode('off')
    }
  }, [comparisonMode, hasEnabledGrade, setComparisonMode])

  const displayEntries = useMemo(() => {
    const wheels =
      findGradeEntry(displayItem, 'gpu-color-wheels') ??
      ({
        id: '__grade:wheels__',
        enabled: true,
        effect: {
          type: 'gpu-effect',
          gpuEffectType: 'gpu-color-wheels',
          params: getGpuEffectDefaultParams('gpu-color-wheels'),
        },
      } satisfies ItemEffect)

    const curves =
      findGradeEntry(displayItem, 'gpu-curves') ??
      ({
        id: '__grade:curves__',
        enabled: true,
        effect: {
          type: 'gpu-effect',
          gpuEffectType: 'gpu-curves',
          params: getGpuEffectDefaultParams('gpu-curves'),
        },
      } satisfies ItemEffect)

    return {
      wheels,
      curves,
    }
  }, [displayItem])

  const getResolvedDisplayGpuEffect = useCallback(
    (effectEntry: ItemEffect): GpuEffect => {
      const gpuEffect = effectEntry.effect as GpuEffect
      if (!displayItem) {
        return gpuEffect
      }

      const definition = getGpuEffect(gpuEffect.gpuEffectType)
      if (!definition) {
        return gpuEffect
      }

      const itemKeyframeState = itemKeyframes.get(displayItem.id) ?? undefined
      const relativeFrame = currentFrame - displayItem.from
      let nextParams = gpuEffect.params
      let changed = false

      for (const [paramKey, param] of Object.entries(definition.params)) {
        if (param.type !== 'number' || !param.animatable) {
          continue
        }

        const value = getResolvedAnimatedEffectParamValue(
          effectEntry,
          itemKeyframeState ?? undefined,
          relativeFrame,
          paramKey,
        )
        if (value === null || nextParams[paramKey] === value) {
          continue
        }

        if (!changed) {
          nextParams = { ...gpuEffect.params }
          changed = true
        }

        nextParams[paramKey] = value
      }

      return changed ? { ...gpuEffect, params: nextParams } : gpuEffect
    },
    [currentFrame, displayItem, itemKeyframes],
  )

  const getKeyframeProperty = useCallback(
    (effectId: string, paramKey: string): AnimatableProperty | null => {
      const effect =
        displayEntries.wheels.id === effectId
          ? displayEntries.wheels
          : displayEntries.curves.id === effectId
            ? displayEntries.curves
            : null

      if (!effect || effect.id.startsWith('__grade:') || effect.effect.type !== 'gpu-effect') {
        return null
      }

      const definition = getGpuEffect(effect.effect.gpuEffectType)
      const param = definition?.params[paramKey]
      if (!definition || param?.type !== 'number' || !param.animatable) {
        return null
      }

      return buildEffectAnimatableProperty(effect.effect.gpuEffectType, effect.id, paramKey)
    },
    [displayEntries.curves, displayEntries.wheels],
  )

  const resolveEffectType = useCallback(
    (effectId: string) => {
      if (effectId === displayEntries.wheels.id) return 'gpu-color-wheels'
      if (effectId === displayEntries.curves.id) return 'gpu-curves'
      return null
    },
    [displayEntries.curves.id, displayEntries.wheels.id],
  )

  const getMappedEntryForItem = useCallback(
    (item: TimelineItem, effectType: (typeof COLOR_GRADE_EFFECT_TYPES)[number]) =>
      findGradeEntry(item, effectType),
    [],
  )

  const handleGradeUndo = useCallback(() => {
    const restored = undoGrade()
    if (restored && Object.keys(restored).length > 0) {
      applyRestoredEffects(restored)
      updateUndoState()
    }
  }, [updateUndoState])

  const handleGradeRedo = useCallback(() => {
    const restored = redoGrade()
    if (restored && Object.keys(restored).length > 0) {
      applyRestoredEffects(restored)
      updateUndoState()
    }
  }, [updateUndoState])

  const handleGpuParamChange = useCallback(
    (effectId: string, paramKey: string, value: number | boolean | string) => {
      const effectType = resolveEffectType(effectId)
      if (!effectType) return

      // Record current effects before change for undo
      const currentEffects: Record<string, ItemEffect[]> = {}
      for (const item of items) {
        currentEffects[item.id] = [...(item.effects ?? [])]
      }
      useColorGradeUndoStore.getState().recordBeforeChange(currentEffects)

      const definition = getGpuEffect(effectType)
      const param = definition?.params[paramKey]
      const autoOperations =
        typeof value === 'number' && definition && param?.type === 'number' && param.animatable
          ? items.flatMap((item) => {
              const targetEffect = getMappedEntryForItem(item, effectType)
              if (!targetEffect || targetEffect.effect.type !== 'gpu-effect') {
                return []
              }

              const itemKeyframeState = itemKeyframes.get(item.id) ?? undefined
              const property = buildEffectAnimatableProperty(
                targetEffect.effect.gpuEffectType,
                targetEffect.id,
                paramKey,
              )
              const operation = getAutoKeyframeOperation(
                item,
                itemKeyframeState ?? undefined,
                property,
                value,
                currentFrame,
              )
              return operation ? [operation] : []
            })
          : []

      if (autoOperations.length > 0) {
        applyAutoKeyframeOperations(autoOperations)
      }

      items.forEach((item) => {
        const targetEffect = getMappedEntryForItem(item, effectType)
        if (!targetEffect || targetEffect.effect.type !== 'gpu-effect') {
          return
        }

        const property = buildEffectAnimatableProperty(
          targetEffect.effect.gpuEffectType,
          targetEffect.id,
          paramKey,
        )
        const autoHandled =
          typeof value === 'number' &&
          autoOperations.some(
            (operation) => operation.itemId === item.id && operation.property === property,
          )
        if (autoHandled) {
          return
        }

        updateEffect(item.id, targetEffect.id, {
          effect: {
            ...targetEffect.effect,
            params: { ...targetEffect.effect.params, [paramKey]: value },
          },
        })
      })
      queueMicrotask(() => clearPreview())
    },
    [
      applyAutoKeyframeOperations,
      clearPreview,
      currentFrame,
      getMappedEntryForItem,
      itemKeyframes,
      items,
      resolveEffectType,
      updateEffect,
    ],
  )

  const handleGpuParamsBatchChange = useCallback(
    (effectId: string, updates: Record<string, number | boolean | string>) => {
      const effectType = resolveEffectType(effectId)
      if (!effectType) return

      // Record current effects before batch change for undo
      const currentEffects: Record<string, ItemEffect[]> = {}
      for (const item of items) {
        currentEffects[item.id] = [...(item.effects ?? [])]
      }
      useColorGradeUndoStore.getState().recordBeforeChange(currentEffects)

      const definition = getGpuEffect(effectType)
      const autoOperations = items.flatMap((item) => {
        const targetEffect = getMappedEntryForItem(item, effectType)
        if (!targetEffect || targetEffect.effect.type !== 'gpu-effect') {
          return []
        }

        const itemKeyframeState = itemKeyframes.get(item.id) ?? undefined

        return Object.entries(updates).flatMap(([paramKey, paramValue]) => {
          const param = definition?.params[paramKey]
          if (
            typeof paramValue !== 'number' ||
            !definition ||
            param?.type !== 'number' ||
            !param.animatable
          ) {
            return []
          }

          const property = buildEffectAnimatableProperty(
            targetEffect.effect.gpuEffectType,
            targetEffect.id,
            paramKey,
          )
          const operation = getAutoKeyframeOperation(
            item,
            itemKeyframeState ?? undefined,
            property,
            paramValue,
            currentFrame,
          )
          return operation ? [operation] : []
        })
      })

      if (autoOperations.length > 0) {
        applyAutoKeyframeOperations(autoOperations)
      }

      items.forEach((item) => {
        const targetEffect = getMappedEntryForItem(item, effectType)
        if (!targetEffect || targetEffect.effect.type !== 'gpu-effect') {
          return
        }

        const fallbackUpdates = { ...updates }
        for (const [paramKey, paramValue] of Object.entries(updates)) {
          const property = buildEffectAnimatableProperty(
            targetEffect.effect.gpuEffectType,
            targetEffect.id,
            paramKey,
          )
          const autoHandled =
            typeof paramValue === 'number' &&
            autoOperations.some(
              (operation) => operation.itemId === item.id && operation.property === property,
            )
          if (autoHandled) {
            delete fallbackUpdates[paramKey]
          }
        }

        if (Object.keys(fallbackUpdates).length === 0) {
          return
        }

        updateEffect(item.id, targetEffect.id, {
          effect: {
            ...targetEffect.effect,
            params: { ...targetEffect.effect.params, ...fallbackUpdates },
          },
        })
      })
      queueMicrotask(() => clearPreview())
    },
    [
      applyAutoKeyframeOperations,
      clearPreview,
      currentFrame,
      getMappedEntryForItem,
      itemKeyframes,
      items,
      resolveEffectType,
      updateEffect,
    ],
  )

  const handleGpuParamLiveChange = useCallback(
    (effectId: string, paramKey: string, value: number | boolean | string) => {
      const effectType = resolveEffectType(effectId)
      if (!effectType) return

      const previews: Record<string, ItemEffect[]> = {}
      items.forEach((item) => {
        const targetEffect = getMappedEntryForItem(item, effectType)
        if (!targetEffect) return
        previews[item.id] = (item.effects ?? []).map((entry) => {
          if (entry.id !== targetEffect.id || entry.effect.type !== 'gpu-effect') return entry
          const entryGpu = entry.effect as GpuEffect
          return {
            ...entry,
            effect: {
              ...entryGpu,
              params: { ...entryGpu.params, [paramKey]: value },
            },
          }
        })
      })
      setEffectsPreviewNew(previews)
    },
    [getMappedEntryForItem, items, resolveEffectType, setEffectsPreviewNew],
  )

  const handleGpuParamsBatchLiveChange = useCallback(
    (effectId: string, updates: Record<string, number | boolean | string>) => {
      const effectType = resolveEffectType(effectId)
      if (!effectType) return

      const previews: Record<string, ItemEffect[]> = {}
      items.forEach((item) => {
        const targetEffect = getMappedEntryForItem(item, effectType)
        if (!targetEffect) return
        previews[item.id] = (item.effects ?? []).map((entry) => {
          if (entry.id !== targetEffect.id || entry.effect.type !== 'gpu-effect') return entry
          const entryGpu = entry.effect as GpuEffect
          return {
            ...entry,
            effect: {
              ...entryGpu,
              params: { ...entryGpu.params, ...updates },
            },
          }
        })
      })
      setEffectsPreviewNew(previews)
    },
    [getMappedEntryForItem, items, resolveEffectType, setEffectsPreviewNew],
  )

  const handleReset = useCallback(
    (effectId: string) => {
      const effectType = resolveEffectType(effectId)
      if (!effectType) return

      // Record current effects before reset for undo
      const currentEffects: Record<string, ItemEffect[]> = {}
      for (const item of items) {
        currentEffects[item.id] = [...(item.effects ?? [])]
      }
      useColorGradeUndoStore.getState().recordBeforeChange(currentEffects)

      items.forEach((item) => {
        const targetEffect = getMappedEntryForItem(item, effectType)
        if (!targetEffect || targetEffect.effect.type !== 'gpu-effect') {
          return
        }

        updateEffect(item.id, targetEffect.id, {
          effect: {
            ...targetEffect.effect,
            params: getGpuEffectDefaultParams(effectType),
          },
        })
      })
    },
    [getMappedEntryForItem, items, resolveEffectType, updateEffect],
  )

  const handleToggle = useCallback(
    (effectId: string) => {
      const effectType = resolveEffectType(effectId)
      if (!effectType) return

      // Record current effects before toggle for undo
      const currentEffects: Record<string, ItemEffect[]> = {}
      for (const item of items) {
        currentEffects[item.id] = [...(item.effects ?? [])]
      }
      useColorGradeUndoStore.getState().recordBeforeChange(currentEffects)

      items.forEach((item) => {
        const targetEffect = getMappedEntryForItem(item, effectType)
        if (targetEffect) {
          toggleEffect(item.id, targetEffect.id)
        }
      })
    },
    [getMappedEntryForItem, items, resolveEffectType, toggleEffect],
  )

  const handleRemove = useCallback(
    (effectId: string) => {
      const effectType = resolveEffectType(effectId)
      if (!effectType) return

      // Record current effects before remove for undo
      const currentEffects: Record<string, ItemEffect[]> = {}
      for (const item of items) {
        currentEffects[item.id] = [...(item.effects ?? [])]
      }
      useColorGradeUndoStore.getState().recordBeforeChange(currentEffects)

      items.forEach((item) => {
        const targetEffect = getMappedEntryForItem(item, effectType)
        if (targetEffect) {
          removeEffect(item.id, targetEffect.id)
        }
      })
    },
    [getMappedEntryForItem, items, removeEffect, resolveEffectType],
  )

  const handleCopyGrade = useCallback(() => {
    if (copyGrade()) {
      toast.success('已复制调色')
    }
  }, [copyGrade])

  const handlePasteGrade = useCallback(() => {
    if (pasteGrade()) {
      toast.success(items.length > 1 ? '已将调色应用到 ' + items.length + ' 个片段' : '已粘贴调色')
    }
  }, [items.length, pasteGrade])

  // Note: handlePasteGrade and handleApplyPreset mutate effects through useColorGradeActions
  // which internally calls updateEffect (already goes through command store).
  // Grade undo recording for these is handled by the next param change.

  const handleSavePreset = useCallback(() => {
    if (!savePreset(presetName)) {
      return
    }

    toast.success('已保存调色预设')
    setPresetName('')
    setPresetPopoverOpen(false)
  }, [presetName, savePreset])

  const handleApplyPreset = useCallback(
    (presetId: string) => {
      // Record current effects before applying preset for undo
      const currentEffects: Record<string, ItemEffect[]> = {}
      for (const item of items) {
        currentEffects[item.id] = [...(item.effects ?? [])]
      }
      useColorGradeUndoStore.getState().recordBeforeChange(currentEffects)

      if (applyPreset(presetId)) {
        toast.success(items.length > 1 ? '已将预设应用到 ' + items.length + ' 个片段' : '已应用调色预设')
      }
    },
    [applyPreset, items, items.length],
  )

  const handleRemovePreset = useCallback(
    (presetId: string) => {
      removePreset(presetId)
      toast.success('已删除调色预设')
    },
    [removePreset],
  )

  const toolbarButtons = useMemo(
    () => [
      { key: 'off', label: '调整后', icon: Eye },
      { key: 'before', label: '调整前', icon: CircleOff },
      { key: 'split', label: '分屏', icon: Columns2, disabled: !hasEnabledGrade },
    ],
    [hasEnabledGrade],
  )

  const wheelsDefinition = getGpuEffect('gpu-color-wheels')
  const curvesDefinition = getGpuEffect('gpu-curves')

  if (!wheelsDefinition || !curvesDefinition) {
    return null
  }

  const wheelsPanel = (
    <GpuWheelsPanel
      itemIds={itemIds}
      effect={displayEntries.wheels}
      gpuEffect={getResolvedDisplayGpuEffect(displayEntries.wheels)}
      definition={wheelsDefinition}
      layout={layout}
      getKeyframeProperty={getKeyframeProperty}
      onParamChange={handleGpuParamChange}
      onParamLiveChange={handleGpuParamLiveChange}
      onParamsBatchChange={handleGpuParamsBatchChange}
      onParamsBatchLiveChange={handleGpuParamsBatchLiveChange}
      onReset={handleReset}
      onToggle={handleToggle}
      onRemove={handleRemove}
    />
  )

  const curvesPanel = (
    <GpuCurvesPanel
      effect={displayEntries.curves}
      gpuEffect={getResolvedDisplayGpuEffect(displayEntries.curves)}
      definition={curvesDefinition}
      layout={layout}
      onParamChange={handleGpuParamChange}
      onParamLiveChange={handleGpuParamLiveChange}
      onParamsBatchChange={handleGpuParamsBatchChange}
      onParamsBatchLiveChange={handleGpuParamsBatchLiveChange}
      onReset={handleReset}
      onToggle={handleToggle}
      onRemove={handleRemove}
    />
  )

  if (layout === 'dock') {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[3px] border border-border/70 bg-background/35">
        <div className="grid shrink-0 gap-2 border-b border-border/70 p-2 lg:grid-cols-[minmax(220px,1fr)_minmax(220px,0.85fr)]">
          <div className="grid grid-cols-3 gap-1" role="group" aria-label="调色对比模式">
            {toolbarButtons.map((button) => {
              const Icon = button.icon
              return (
                <Button
                  key={button.key}
                  variant={comparisonMode === button.key ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-1 text-xs"
                  disabled={button.disabled}
                  title={
                    button.key === 'split' && button.disabled
                      ? '启用调色后可分屏对比'
                      : undefined
                  }
                  onClick={() => setComparisonMode(button.key as 'off' | 'before' | 'split')}
                >
                  <Icon className="mr-1 h-3 w-3 shrink-0" />
                  {button.label}
                </Button>
              )
            })}
          </div>

          <div className="flex flex-wrap items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleGradeUndo}
              disabled={!canGradeUndo}
              aria-label="撤销调色"
              title="撤销调色 (Ctrl+Z)"
            >
              <Undo2 className="mr-1 h-3 w-3 shrink-0" />
              撤销
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleGradeRedo}
              disabled={!canGradeRedo}
              aria-label="重做调色"
              title="重做调色 (Ctrl+Shift+Z)"
            >
              <Redo2 className="mr-1 h-3 w-3 shrink-0" />
              重做
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 min-w-[5.75rem] flex-1 text-xs"
              onClick={handleCopyGrade}
              disabled={!canCopyGrade}
              title="复制调色"
            >
              <Copy className="mr-1 h-3 w-3 shrink-0" />
              复制调色
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 min-w-[5.75rem] flex-1 text-xs"
              onClick={handlePasteGrade}
              disabled={!canPasteGrade}
              title="粘贴调色"
            >
              <ClipboardPaste className="mr-1 h-3 w-3 shrink-0" />
              粘贴调色
            </Button>
            {onCreateAdjustmentLayer ? (
              <Button
                variant="outline"
                size="sm"
                className="h-7 min-w-[6.5rem] flex-1 text-xs"
                onClick={onCreateAdjustmentLayer}
                title="新建调整图层用于场景级调色"
              >
                <Layers3 className="mr-1 h-3 w-3 shrink-0" />
                调整图层
              </Button>
            ) : null}
            <Button
              variant={colorScopesOpen ? 'default' : 'outline'}
              size="sm"
              className="h-7 min-w-[5.5rem] flex-1 text-xs"
              onClick={toggleColorScopesOpen}
              title={colorScopesOpen ? '隐藏示波器' : '显示示波器'}
            >
              <LineChart className="mr-1 h-3 w-3 shrink-0" />
              {colorScopesOpen ? '隐藏示波器' : '示波器'}
            </Button>
            <Popover open={presetPopoverOpen} onOpenChange={setPresetPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={presetPopoverOpen ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2"
                  title="保存当前调色为预设"
                  aria-label="调色预设"
                >
                  <Save className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 space-y-3 p-3">
                <div className="space-y-2">
                  <div className="text-xs font-medium text-foreground">保存当前调色</div>
                  <div className="flex gap-2">
                    <Input
                      value={presetName}
                      onChange={(event) => setPresetName(event.target.value)}
                      placeholder="输入预设名称"
                      className="h-8 text-xs"
                    />
                    <Button
                      size="sm"
                      className="h-8 px-3 text-xs"
                      disabled={!canSavePreset || presetName.trim().length === 0}
                      onClick={handleSavePreset}
                    >
                      保存
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-medium text-foreground">我的预设</div>
                  {presets.length > 0 ? (
                    <div className="max-h-56 space-y-1 overflow-auto pr-1">
                      {presets.map((preset) => (
                        <div
                          key={preset.id}
                          className="flex items-center gap-2 rounded-md border border-border/70 px-2 py-1.5"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-xs font-medium text-foreground">
                              {preset.name}
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              {preset.grade.length} 个调色模块
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleApplyPreset(preset.id)}
                            aria-label={`应用预设 ${preset.name}`}
                          >
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => handleRemovePreset(preset.id)}
                            aria-label={`删除预设 ${preset.name}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-md border border-dashed border-border/70 px-3 py-4 text-xs text-muted-foreground">
                      还没有保存过调色预设。
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
          <section className="flex h-full min-h-0 flex-col overflow-hidden border-r border-border/70">
            <div className="min-h-0 flex-1 overflow-hidden">{wheelsPanel}</div>
          </section>
          <section className="flex h-full min-h-0 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-hidden">{curvesPanel}</div>
          </section>
        </div>
      </div>
    )
  }

  return (
    <section className="overflow-hidden rounded-md border border-border/70 bg-background/70">
      <div className="border-b border-border/70 px-3 py-2">
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <Palette className="h-3.5 w-3.5" aria-hidden="true" />
            <span>调色主控</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-30"
              onClick={handleGradeUndo}
              disabled={!canGradeUndo}
              aria-label="撤销调色"
              title="撤销 (Ctrl+Z)"
            >
              <Undo2 className="h-3.5 w-3.5" />
            </button>
            <button
              className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-30"
              onClick={handleGradeRedo}
              disabled={!canGradeRedo}
              aria-label="重做调色"
              title="重做 (Ctrl+Shift+Z)"
            >
              <Redo2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
      <div className="space-y-0">
        {wheelsPanel}
        {curvesPanel}
      </div>
    </section>
  )
})
